<script setup lang="ts">
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { QuestionMarkCircleIcon } from '@heroicons/vue/24/outline'
import type { Component } from 'vue'
import { useSlots } from 'vue'

withDefaults(defineProps<{
    appear?: boolean,
    icon: Component,
    color?: string,
    hintAreaLabel?: string,
}>(), {
    appear: false,
    color: 'secondary',
    hintAreaLabel: undefined,
})

const slots = useSlots()
</script>

<template>
    <Popover class="relative">
        <Transition
            :appear="appear"
            enter-active-class="transition duration-300 ease-out"
            enter-from-class="scale-95 opacity-0"
            enter-to-class="scale-100 opacity-100"
            leave-active-class="transition duration-200 ease-in"
            leave-from-class="scale-100 opacity-100"
            leave-to-class="scale-95 opacity-0">
            <div :class="['flex items-center p-4 rounded gap-x-4 text-grayscale-800 max-h-14', color === 'secondary' && 'bg-grayscale-50', color === 'mint' && 'bg-mint-50']">
                <component :is="icon" class="flex-shrink-0 w-6 h-6 stroke-[1.25]" />
                <div class="flex-1 font-medium text-p2">
                    <slot />
                </div>

                <PopoverButton v-if="slots.hint" v-slot="{ open }" class="focus:outline-none" :aria-label="hintAreaLabel">
                    <QuestionMarkCircleIcon :class="['flex-shrink-0 w-6 h-6 stroke-[1.25] transition hover:fill-grayscale-800 hover:stroke-white hover:scale-125', open && 'fill-grayscale-800 stroke-white scale-125']" />
                </PopoverButton>
            </div>
        </Transition>

        <Transition enter-active-class="transition duration-100 ease-out" enter-from-class="transform scale-95 opacity-0" enter-to-class="transform scale-100 opacity-100" leave-active-class="transition duration-75 ease-in" leave-from-class="transform scale-100 opacity-100" leave-to-class="transform scale-95 opacity-0">
            <PopoverPanel class="absolute right-0 max-w-sm p-4 mt-4 whitespace-pre-line origin-top-right bg-white rounded shadow-2xl text-grayscale-800 text-p3 ring-1 ring-grayscale-100 focus:outline-none">
                <div class="right-6 absolute -top-1.5 rotate-45 w-2.5 h-2.5 bg-white border-l border-t border-grayscale-100"></div>
                <slot name="hint" />
            </PopoverPanel>
        </Transition>
    </Popover>
</template>
